import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { FileText, Upload, Package, CheckCircle, AlertCircle, Clock, ExternalLink, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [artworkFolderUrl, setArtworkFolderUrl] = useState("");
  const [artworkFolderUrls, setArtworkFolderUrls] = useState(["", "", "", "", ""]);  // 5 folder URLs for 5 items
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [erpEndpoint] = useState("https://api.swagify.com/public/api/custom/new-order"); // Hardcoded
  const [erpPayloadPreview, setErpPayloadPreview] = useState(null);
  const [showPayloadPreview, setShowPayloadPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Artwork file states - now per item (array of artwork sets)
  const [itemArtworks, setItemArtworks] = useState([]);
  
  // State to track manual artwork type changes
  const [artworkTypeChanges, setArtworkTypeChanges] = useState({});

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");

  // Check authentication on component mount
  useEffect(() => {
    const authStatus = localStorage.getItem('swagify_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch invoices only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchInvoices();
    }
  }, [isAuthenticated]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to fetch invoices");
    }
  };

  // Handle login
  const handleLogin = (e) => {
    e.preventDefault();
    const correctPassword = '$wagFl0#';
    
    if (password === correctPassword) {
      setIsAuthenticated(true);
      localStorage.setItem('swagify_authenticated', 'true');
      setAuthError("");
      setPassword("");
    } else {
      setAuthError("Incorrect password. Please try again.");
      setPassword("");
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('swagify_authenticated');
    toast.info("Logged out successfully");
  };

  const handleProcessInvoice = async () => {
    if (!invoiceUrl.trim()) {
      toast.error("Please enter a Google Drive invoice URL");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/process-invoice`, {
        invoice_url: invoiceUrl,
        artwork_folder_urls: artworkFolderUrls.filter(url => url.trim() !== "")
      });

      toast.success("Invoice processed successfully!");
      
      // Reset all input fields for next invoice
      setInvoiceUrl("");
      setArtworkFolderUrl("");
      setArtworkFolderUrls(["", "", "", "", ""]);
      
      fetchInvoices();
      setSelectedInvoice(response.data);
      
      // Initialize artwork arrays for each item
      const itemCount = response.data.invoice_data.items?.length || 0;
      const initialArtworks = Array(itemCount).fill(null).map(() => ({
        front_art: { filename: "", url: "" },
        back_art: { filename: "", url: "" },
        sleeve_art: { filename: "", url: "" },
        front_mockup: { filename: "", url: "" },
        back_mockup: { filename: "", url: "" }
      }));
      setItemArtworks(initialArtworks);
    } catch (error) {
      console.error("Error processing invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to process invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleAddArtworkFiles = async (itemIndex) => {
    if (!selectedInvoice) {
      toast.error("No invoice selected");
      return;
    }

    // Collect non-empty artwork files for this specific item
    const artworkFilesForItem = itemArtworks[itemIndex];
    const filesToAdd = [];
    
    Object.entries(artworkFilesForItem).forEach(([type, data]) => {
      if (data.url.trim()) {
        filesToAdd.push({
          type: type,
          filename: data.filename || `${type}.${type.includes('mockup') ? 'jpg' : 'png'}`,
          url: data.url,
          item_index: itemIndex
        });
      }
    });

    if (filesToAdd.length === 0) {
      toast.error("Please add at least one artwork file");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/add-artwork-files`, {
        invoice_id: selectedInvoice.id,
        artwork_files: filesToAdd,
        item_index: itemIndex
      });

      toast.success(`Added ${filesToAdd.length} artwork files for item ${itemIndex + 1}`);
      
      // Refresh the invoice data
      const response = await axios.get(`${API}/invoices/${selectedInvoice.id}`);
      setSelectedInvoice(response.data);
      fetchInvoices();
    } catch (error) {
      console.error("Error adding artwork files:", error);
      toast.error(error.response?.data?.detail || "Failed to add artwork files");
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewPayload = async () => {
    if (!selectedInvoice) {
      toast.error("No invoice selected");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/preview-erp-payload`, {
        invoice_id: selectedInvoice.id
      });

      setErpPayloadPreview(response.data.payload_json);
      setShowPayloadPreview(true);
      toast.success("Payload generated successfully!");
    } catch (error) {
      console.error("Error generating payload:", error);
      toast.error(error.response?.data?.detail || "Failed to generate payload");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitToERP = async (invoiceId) => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/submit-to-erp`, {
        invoice_id: invoiceId,
        erp_endpoint: erpEndpoint,
        erp_api_key: null
      });

      if (response.data.success) {
        toast.success("✅ Order submitted to ERP successfully!");
        // Refresh the invoices to get updated status
        await fetchInvoices();
        // Refresh the selected invoice
        const updatedResponse = await axios.get(`${API}/invoices/${invoiceId}`);
        setSelectedInvoice(updatedResponse.data);
      } else {
        toast.error("❌ ERP submission failed");
      }
    } catch (error) {
      console.error("Error submitting to ERP:", error);
      toast.error(error.response?.data?.detail || "❌ Failed to submit to ERP");
    } finally {
      setSubmitting(false);
    }
  };

  const handleArtworkTypeChange = (itemIndex, fileId, newType) => {
    // Validation: Check for duplicate categories in the same item
    if (selectedInvoice && selectedInvoice.item_artworks) {
      const itemArtworks = selectedInvoice.item_artworks[itemIndex];
      
      if (itemArtworks) {
        // Check if another file in this item already has the same type
        const duplicateExists = itemArtworks.some(art => 
          art.file_id !== fileId && art.type === newType
        );
        
        if (duplicateExists) {
          const typeLabels = {
            'front_art': 'Front',
            'back_art': 'Back',
            'sleeve_art': 'Sleeve',
            'front_mockup': 'Front Preview',
            'back_mockup': 'Back Preview'
          };
          
          toast.error(`❌ Item ${itemIndex + 1} already has a ${typeLabels[newType]} image. Each item can only have one of each type.`);
          return; // Don't allow the change
        }
      }
    }
    
    // Track the change in state
    setArtworkTypeChanges(prev => ({
      ...prev,
      [itemIndex]: {
        ...(prev[itemIndex] || {}),
        [fileId]: newType
      }
    }));
    
    // Also update the displayed artwork in selectedInvoice
    if (selectedInvoice && selectedInvoice.item_artworks) {
      const updatedInvoice = { ...selectedInvoice };
      const itemArtworks = updatedInvoice.item_artworks[itemIndex];
      
      if (itemArtworks) {
        const updatedArtworks = itemArtworks.map(art => 
          art.file_id === fileId ? { ...art, type: newType } : art
        );
        updatedInvoice.item_artworks[itemIndex] = updatedArtworks;
        setSelectedInvoice(updatedInvoice);
      }
    }
  };

  const handleSaveArtworkTypes = async (itemIndex) => {
    if (!selectedInvoice || !artworkTypeChanges[itemIndex]) {
      return;
    }

    // Validation: Check for duplicate categories before saving
    if (selectedInvoice.item_artworks && selectedInvoice.item_artworks[itemIndex]) {
      const itemArtworks = selectedInvoice.item_artworks[itemIndex];
      const typeCounts = {};
      
      // Count occurrences of each type
      itemArtworks.forEach(art => {
        typeCounts[art.type] = (typeCounts[art.type] || 0) + 1;
      });
      
      // Check if any type appears more than once
      const duplicates = Object.entries(typeCounts).filter(([type, count]) => count > 1);
      
      if (duplicates.length > 0) {
        const typeLabels = {
          'front_art': 'Front',
          'back_art': 'Back',
          'sleeve_art': 'Sleeve',
          'front_mockup': 'Front Preview',
          'back_mockup': 'Back Preview'
        };
        
        const duplicateTypes = duplicates.map(([type]) => typeLabels[type]).join(', ');
        toast.error(`❌ Cannot save: Item ${itemIndex + 1} has duplicate categories (${duplicateTypes}). Each type can only be used once per item.`);
        return; // Don't save
      }
    }

    setLoading(true);
    try {
      const changes = artworkTypeChanges[itemIndex];
      const artwork_updates = Object.entries(changes).map(([file_id, new_type]) => ({
        file_id,
        new_type
      }));

      const response = await axios.put(`${API}/update-artwork-types`, {
        invoice_id: selectedInvoice.id,
        item_index: itemIndex,
        artwork_updates
      });

      if (response.data.success) {
        toast.success(`✅ Saved ${response.data.updated_count} artwork type(s) for Item ${itemIndex + 1}`);
        
        // Clear the changes for this item
        setArtworkTypeChanges(prev => {
          const updated = { ...prev };
          delete updated[itemIndex];
          return updated;
        });
        
        // Refresh the invoice to get updated data
        const invoiceResponse = await axios.get(`${API}/invoices/${selectedInvoice.id}`);
        setSelectedInvoice(invoiceResponse.data);
      }
    } catch (error) {
      console.error("Error saving artwork types:", error);
      toast.error(error.response?.data?.detail || "Failed to save artwork types");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      processed: { label: "Processed", variant: "default", icon: CheckCircle, color: "bg-blue-100 text-blue-700 border-blue-200" },
      submitted: { label: "Submitted to ERP", variant: "default", icon: CheckCircle, color: "bg-green-100 text-green-700 border-green-200" },
      failed: { label: "Submission Failed", variant: "destructive", icon: AlertCircle, color: "bg-red-100 text-red-700 border-red-200" },
      error: { label: "Processing Error", variant: "destructive", icon: AlertCircle, color: "bg-red-100 text-red-700 border-red-200" }
    };
    
    const config = configs[status] || { label: status, variant: "secondary", icon: Clock, color: "bg-gray-100 text-gray-700 border-gray-200" };
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={`${config.color} flex items-center gap-1 px-2 py-1`}>
        <Icon size={14} />
        {config.label}
      </Badge>
    );
  };

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <img 
                src="https://buhoops.com/cdn/shop/files/patch_label_5_notrademark_540x.jpg?v=1646835368" 
                alt="Logo" 
                className="h-20 w-auto object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Access Required</CardTitle>
            <CardDescription className="text-slate-600">
              Enter password to access the invoice processing system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full"
                  autoFocus
                />
                {authError && (
                  <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {authError}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={!password}>
                Access System
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12 relative">
          {/* Logout button in top right */}
          <div className="absolute right-0 top-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4" />
              Logout
            </Button>
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://buhoops.com/cdn/shop/files/patch_label_5_notrademark_540x.jpg?v=1646835368" 
              alt="Logo" 
              className="h-24 w-auto object-contain"
            />
          </div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Upload invoice and artfiles from Google Drive, manually review the extracted data then submit for Order Processing
          </p>
        </div>

        <Tabs defaultValue="process" className="space-y-8">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="process" data-testid="process-tab">
              <Upload className="w-4 h-4 mr-2" />
              Process Invoice
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <Package className="w-4 h-4 mr-2" />
              Invoice History
            </TabsTrigger>
          </TabsList>

          {/* Process Invoice Tab */}
          <TabsContent value="process" data-testid="process-tab-content">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Upload Section */}
              <Card className="shadow-xl border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Upload Invoice
                  </CardTitle>
                  <CardDescription>
                    Paste Google Drive URLs to process invoice PDFs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="invoice-url">
                      Invoice PDF URL *
                    </label>
                    <Input
                      id="invoice-url"
                      data-testid="invoice-url-input"
                      placeholder="https://drive.google.com/file/d/..."
                      value={invoiceUrl}
                      onChange={(e) => setInvoiceUrl(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="artwork-url-1">
                      Item 1 Artwork Folder URL (Optional)
                    </label>
                    <Input
                      id="artwork-url-1"
                      data-testid="artwork-url-input-1"
                      placeholder="https://drive.google.com/drive/folders/..."
                      value={artworkFolderUrls[0]}
                      onChange={(e) => {
                        const newUrls = [...artworkFolderUrls];
                        newUrls[0] = e.target.value;
                        setArtworkFolderUrls(newUrls);
                      }}
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="artwork-url-2">
                      Item 2 Artwork Folder URL (Optional)
                    </label>
                    <Input
                      id="artwork-url-2"
                      data-testid="artwork-url-input-2"
                      placeholder="https://drive.google.com/drive/folders/..."
                      value={artworkFolderUrls[1]}
                      onChange={(e) => {
                        const newUrls = [...artworkFolderUrls];
                        newUrls[1] = e.target.value;
                        setArtworkFolderUrls(newUrls);
                      }}
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="artwork-url-3">
                      Item 3 Artwork Folder URL (Optional)
                    </label>
                    <Input
                      id="artwork-url-3"
                      data-testid="artwork-url-input-3"
                      placeholder="https://drive.google.com/drive/folders/..."
                      value={artworkFolderUrls[2]}
                      onChange={(e) => {
                        const newUrls = [...artworkFolderUrls];
                        newUrls[2] = e.target.value;
                        setArtworkFolderUrls(newUrls);
                      }}
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="artwork-url-4">
                      Item 4 Artwork Folder URL (Optional)
                    </label>
                    <Input
                      id="artwork-url-4"
                      data-testid="artwork-url-input-4"
                      placeholder="https://drive.google.com/drive/folders/..."
                      value={artworkFolderUrls[3]}
                      onChange={(e) => {
                        const newUrls = [...artworkFolderUrls];
                        newUrls[3] = e.target.value;
                        setArtworkFolderUrls(newUrls);
                      }}
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="artwork-url-5">
                      Item 5 Artwork Folder URL (Optional)
                    </label>
                    <Input
                      id="artwork-url-5"
                      data-testid="artwork-url-input-5"
                      placeholder="https://drive.google.com/drive/folders/..."
                      value={artworkFolderUrls[4]}
                      onChange={(e) => {
                        const newUrls = [...artworkFolderUrls];
                        newUrls[4] = e.target.value;
                        setArtworkFolderUrls(newUrls);
                      }}
                      disabled={loading}
                    />
                  </div>

                  <Button
                    data-testid="process-invoice-btn"
                    onClick={handleProcessInvoice}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    size="lg"
                  >
                    {loading ? "Processing..." : "Process Invoice"}
                  </Button>

                  <div className="text-xs text-slate-500 space-y-1 pt-2">
                    <p>• Make sure the Google Drive invoice file is publicly accessible</p>
                    <p>• Provide up to 5 artwork folder URLs (one per item in the order)</p>
                    <p>• Each folder should contain 5 files: 3 PNG (front/back/sleeve) + 2 JPG (mockups)</p>
                  </div>
                </CardContent>
              </Card>

              {/* Preview Section */}
              {selectedInvoice && (
                <Card className="shadow-xl border-slate-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Extracted Data
                      </CardTitle>
                      {getStatusBadge(selectedInvoice.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="font-semibold text-slate-700">Order #:</span>
                        <p className="text-slate-600">{selectedInvoice.invoice_data.order_number || "N/A"}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-slate-700">Date:</span>
                        <p className="text-slate-600">{selectedInvoice.invoice_data.date || selectedInvoice.invoice_data.invoice_date || "N/A"}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="font-semibold text-slate-700">Shipping Address:</span>
                          <p className="text-slate-600 whitespace-pre-line text-sm">{selectedInvoice.invoice_data.shipping_address || "N/A"}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-slate-700">Billing Address:</span>
                          <p className="text-slate-600 whitespace-pre-line text-sm">{selectedInvoice.invoice_data.billing_address || "N/A"}</p>
                        </div>
                      </div>
                      
                      <div>
                        <span className="font-semibold text-slate-700">Customer:</span>
                        <p className="text-slate-600">{selectedInvoice.invoice_data.customer_name || "N/A"}</p>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <span className="font-semibold text-slate-700 block mb-3">Products:</span>
                        <div className="space-y-3">
                          {selectedInvoice.invoice_data.items?.map((item, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded border border-slate-200">
                              <div className="space-y-2">
                                <p className="font-medium text-slate-700">{item.product_name || item.description || "N/A"}</p>
                                <div className="text-xs text-slate-600 space-y-0.5">
                                  {item.product_sku && <p>SKU: {item.product_sku}</p>}
                                  {item.product_size && <p>Size: {item.product_size}</p>}
                                  <p>Qty: {item.quantity || "N/A"}</p>
                                </div>
                                
                                {/* Item Artfiles Section */}
                                <div className="mt-3 pt-3 border-t border-slate-200">
                                  <span className="font-semibold text-slate-700 text-xs block mb-2">Item {idx + 1} Artfiles:</span>
                                  
                                  {selectedInvoice.item_artworks && selectedInvoice.item_artworks[idx] && selectedInvoice.item_artworks[idx].length > 0 ? (
                                    <>
                                      <div className="grid grid-cols-2 gap-2">
                                        {selectedInvoice.item_artworks[idx].map((file, fileIdx) => {
                                          const getThumbnailUrl = (url, fileId) => {
                                            if (fileId) {
                                              return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
                                            }
                                            return url;
                                          };
                                          
                                          const thumbnailUrl = getThumbnailUrl(file.url, file.file_id);
                                          
                                          return (
                                            <div key={fileIdx} className="flex flex-col gap-2 p-2 bg-green-50 border border-green-200 rounded">
                                              <div className="relative w-full aspect-square bg-white rounded overflow-hidden">
                                                <img 
                                                  src={thumbnailUrl}
                                                  alt={file.type}
                                                  className="w-full h-full object-contain"
                                                  loading="lazy"
                                                  onError={(e) => {
                                                    e.target.onerror = null;
                                                    if (e.target.src.includes('thumbnail')) {
                                                      e.target.src = `https://drive.google.com/uc?id=${file.file_id}`;
                                                    } else {
                                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTY5Njk2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+';
                                                    }
                                                  }}
                                                />
                                              </div>
                                              <div className="flex flex-col gap-2">
                                                <div className="flex items-start gap-1">
                                                  <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                                                  <div className="flex-1 min-w-0">
                                                    <p className="text-green-700 text-xs truncate" title={file.filename}>{file.filename}</p>
                                                    <span className="text-xs text-green-600 uppercase">{file.file_format}</span>
                                                  </div>
                                                </div>
                                                
                                                {/* Dropdown for manual categorization */}
                                                <Select 
                                                  value={file.type} 
                                                  onValueChange={(value) => handleArtworkTypeChange(idx, file.file_id, value)}
                                                >
                                                  <SelectTrigger className="h-8 text-xs">
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="front_art">Front</SelectItem>
                                                    <SelectItem value="back_art">Back</SelectItem>
                                                    <SelectItem value="sleeve_art">Sleeve</SelectItem>
                                                    <SelectItem value="front_mockup">Front Preview</SelectItem>
                                                    <SelectItem value="back_mockup">Back Preview</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      
                                      {/* Save button - only show if there are unsaved changes for this item */}
                                      {artworkTypeChanges[idx] && Object.keys(artworkTypeChanges[idx]).length > 0 && (
                                        <div className="mt-3">
                                          <Button
                                            size="sm"
                                            onClick={() => handleSaveArtworkTypes(idx)}
                                            disabled={loading}
                                            className="w-full"
                                          >
                                            <Save className="w-3 h-3 mr-2" />
                                            Save Item {idx + 1} Categories
                                          </Button>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="text-xs text-slate-500 p-3 bg-slate-50 rounded border border-slate-200">
                                      <p>No artwork files loaded for this item.</p>
                                      <p className="mt-1">Make sure Item {idx + 1} Artwork Folder URL was provided when processing the invoice.</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <span className="font-semibold text-slate-700">Total:</span>
                        <p className="text-slate-600">${selectedInvoice.invoice_data.total?.toFixed(2) || "0.00"}</p>
                      </div>
                    </div>

                    <Separator />

                    <Button
                      data-testid="submit-erp-btn"
                      onClick={() => handleSubmitToERP(selectedInvoice.id)}
                      disabled={submitting || selectedInvoice.status === "submitted"}
                      className={`w-full ${
                        selectedInvoice.status === "submitted" 
                          ? "bg-green-600 hover:bg-green-600" 
                          : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      }`}
                      size="lg"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4 animate-spin" />
                          Submitting to ERP...
                        </span>
                      ) : selectedInvoice.status === "submitted" ? (
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Already Submitted
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Submit to ERP
                        </span>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Invoice History Tab */}
          <TabsContent value="history" data-testid="history-tab-content">
            <div className="grid gap-4">
              {invoices.length === 0 ? (
                <Card className="shadow-lg">
                  <CardContent className="py-12 text-center">
                    <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 text-lg">No invoices processed yet</p>
                    <p className="text-slate-400 text-sm mt-2">Start by processing your first invoice</p>
                  </CardContent>
                </Card>
              ) : (
                invoices.map((invoice) => (
                  <Card key={invoice.id} className="shadow-lg hover:shadow-xl transition-shadow" data-testid={`invoice-card-${invoice.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <CardTitle className="text-lg">
                            Order #{invoice.invoice_data.order_number || "Unknown"}
                          </CardTitle>
                          <CardDescription>
                            {invoice.invoice_data.customer_name || "No customer name"} • {invoice.invoice_data.date || invoice.invoice_data.invoice_date || "No date"}
                          </CardDescription>
                        </div>
                        {getStatusBadge(invoice.status)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 text-sm mb-4">
                        {invoice.invoice_data.shipping_address && (
                          <div>
                            <span className="font-semibold text-slate-700">Shipping:</span>
                            <p className="text-slate-600 text-xs">{invoice.invoice_data.shipping_address}</p>
                          </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <span className="font-semibold text-slate-700">Products:</span>
                            <p className="text-slate-600">{invoice.invoice_data.items?.length || 0} items</p>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">Total:</span>
                            <p className="text-slate-600">${invoice.invoice_data.total?.toFixed(2) || "0.00"}</p>
                          </div>
                        </div>
                        
                        {invoice.artwork_files && invoice.artwork_files.length > 0 && (
                          <div className="pt-2 border-t border-slate-200">
                            <span className="font-semibold text-slate-700 text-xs block mb-1">Artwork Files ({invoice.artwork_files.length}/5):</span>
                            <div className="flex flex-wrap gap-1">
                              {invoice.artwork_files.map((file, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                  {file.type.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {invoice.invoice_data.items && invoice.invoice_data.items.length > 0 && (
                          <div className="pt-2 border-t border-slate-200">
                            <span className="font-semibold text-slate-700 text-xs block mb-1">Products:</span>
                            <div className="space-y-1">
                              {invoice.invoice_data.items.slice(0, 3).map((item, idx) => (
                                <div key={idx} className="text-xs text-slate-600">
                                  • {item.product_name || item.description} 
                                  {item.product_sku && <span className="text-slate-500"> (SKU: {item.product_sku})</span>}
                                  {item.product_size && <span className="text-slate-500"> - {item.product_size}</span>}
                                  <span className="text-slate-500"> x{item.quantity}</span>
                                </div>
                              ))}
                              {invoice.invoice_data.items.length > 3 && (
                                <p className="text-xs text-slate-500">+ {invoice.invoice_data.items.length - 3} more items</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {invoice.error_message && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700">{invoice.error_message}</p>
                        </div>
                      )}
                      
                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedInvoice(invoice)}
                          data-testid={`view-details-btn-${invoice.id}`}
                        >
                          View Details
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                        >
                          <a href={invoice.invoice_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Open PDF
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;